import { create } from 'zustand';
import { ParsedWorkflow, FlowNode } from '../types/zapier';

interface AppState {
  // License/unlock state
  isUnlocked: boolean;
  licenseKey: string | null;
  setIsUnlocked: (unlocked: boolean) => void;
  setLicenseKey: (key: string | null) => void;

  // Workflow state
  workflows: ParsedWorkflow[];
  currentWorkflowId: string | null;
  setWorkflows: (workflows: ParsedWorkflow[]) => void;
  setCurrentWorkflow: (id: string | null) => void;
  getCurrentWorkflow: () => ParsedWorkflow | null;

  // Node selection
  selectedNode: FlowNode | null;
  setSelectedNode: (node: FlowNode | null) => void;

  // UI state
  isDarkMode: boolean;
  toggleDarkMode: () => void;
  isDetailPanelOpen: boolean;
  setDetailPanelOpen: (open: boolean) => void;

  // File upload state
  fileName: string | null;
  setFileName: (name: string | null) => void;

  // Error handling
  error: string | null;
  setError: (error: string | null) => void;

  // Loading state
  isLoading: boolean;
  loadingProgress: number; // 0-100
  loadingMessage: string;
  setLoadingState: (isLoading: boolean, progress?: number, message?: string) => void;

  // Variable hover state (for visual connections)
  hoveredVariableSource: string | null; // Node ID being highlighted as variable source
  setHoveredVariableSource: (nodeId: string | null) => void;

  // Enhanced variable connection tracking (for drawing connection lines)
  hoveredVariableConnection: {
    sourceNodeId: string;
    targetNodeId: string;
    dataType: 'text' | 'number' | 'array' | 'object' | 'boolean' | 'unknown';
  } | null;
  setHoveredVariableConnection: (connection: {
    sourceNodeId: string;
    targetNodeId: string;
    dataType: 'text' | 'number' | 'array' | 'object' | 'boolean' | 'unknown';
  } | null) => void;

  // Reset state
  reset: () => void;
}

// Load persisted state from localStorage
const loadPersistedState = () => {
  try {
    const savedLicenseKey = localStorage.getItem('zaplens_license_key');
    const savedIsUnlocked = localStorage.getItem('zaplens_is_unlocked') === 'true';
    const savedIsDarkMode = localStorage.getItem('zaplens_dark_mode') === 'true';

    return {
      licenseKey: savedLicenseKey,
      isUnlocked: savedIsUnlocked,
      isDarkMode: savedIsDarkMode,
    };
  } catch (error) {
    console.error('Failed to load persisted state:', error);
    return {
      licenseKey: null,
      isUnlocked: false,
      isDarkMode: false,
    };
  }
};

const persistedState = loadPersistedState();

export const useStore = create<AppState>((set, get) => ({
  // License/unlock state
  isUnlocked: persistedState.isUnlocked,
  licenseKey: persistedState.licenseKey,
  setIsUnlocked: (unlocked: boolean) => {
    set({ isUnlocked: unlocked });
    try {
      localStorage.setItem('zaplens_is_unlocked', unlocked.toString());
    } catch (error) {
      console.error('Failed to persist unlock state:', error);
    }
  },
  setLicenseKey: (key: string | null) => {
    set({ licenseKey: key });
    try {
      if (key) {
        localStorage.setItem('zaplens_license_key', key);
      } else {
        localStorage.removeItem('zaplens_license_key');
      }
    } catch (error) {
      console.error('Failed to persist license key:', error);
    }
  },

  // Workflow state
  workflows: [],
  currentWorkflowId: null,
  setWorkflows: (workflows: ParsedWorkflow[]) => {
    set({ workflows });
    // Auto-select first workflow if none selected
    const state = get();
    if (workflows.length > 0 && !state.currentWorkflowId) {
      set({ currentWorkflowId: workflows[0].id });
    }
  },
  setCurrentWorkflow: (id: string | null) => {
    set({ currentWorkflowId: id, selectedNode: null });
  },
  getCurrentWorkflow: () => {
    const state = get();
    return state.workflows.find(w => w.id === state.currentWorkflowId) || null;
  },

  // Node selection
  selectedNode: null,
  setSelectedNode: (node: FlowNode | null) => {
    set({ selectedNode: node, isDetailPanelOpen: node !== null });
  },

  // UI state
  isDarkMode: persistedState.isDarkMode,
  toggleDarkMode: () => {
    const newMode = !get().isDarkMode;
    set({ isDarkMode: newMode });
    try {
      localStorage.setItem('zaplens_dark_mode', newMode.toString());
      // Update document class for Tailwind dark mode
      if (newMode) {
        document.documentElement.classList.add('dark');
      } else {
        document.documentElement.classList.remove('dark');
      }
    } catch (error) {
      console.error('Failed to persist dark mode:', error);
    }
  },
  isDetailPanelOpen: false,
  setDetailPanelOpen: (open: boolean) => {
    set({ isDetailPanelOpen: open });
    if (!open) {
      set({ selectedNode: null });
    }
  },

  // File upload state
  fileName: null,
  setFileName: (name: string | null) => {
    set({ fileName: name });
  },

  // Error handling
  error: null,
  setError: (error: string | null) => {
    set({ error });
  },

  // Loading state
  isLoading: false,
  loadingProgress: 0,
  loadingMessage: '',
  setLoadingState: (isLoading: boolean, progress = 0, message = '') => {
    set({
      isLoading,
      loadingProgress: Math.min(100, Math.max(0, progress)),
      loadingMessage: message,
    });
  },

  // Variable hover state
  hoveredVariableSource: null,
  setHoveredVariableSource: (nodeId: string | null) => {
    set({ hoveredVariableSource: nodeId });
  },

  // Enhanced variable connection tracking
  hoveredVariableConnection: null,
  setHoveredVariableConnection: (connection) => {
    set({
      hoveredVariableConnection: connection,
      // Also set the simple source for backward compatibility
      hoveredVariableSource: connection?.sourceNodeId || null
    });
  },

  // Reset state
  reset: () => {
    set({
      workflows: [],
      currentWorkflowId: null,
      selectedNode: null,
      fileName: null,
      error: null,
      isDetailPanelOpen: false,
      isLoading: false,
      loadingProgress: 0,
      loadingMessage: '',
    });
  },
}));

// Initialize dark mode on load
if (persistedState.isDarkMode) {
  document.documentElement.classList.add('dark');
}
